<template>
	<LayoutScreen background="--background-base" max-width="500px">
		<template #header>
			<LayoutFlex padding="0 var(--size-20)" gap="0">
				<CancelHeader />
			</LayoutFlex>
		</template>

		<template #content>
			<!-- Figma 7415:18411: the title 24 under the logo row; title, card and list 20 apart; the button in
			     the page 24 under the list. -->
			<LayoutFlex padding="var(--size-24) var(--size-20)" gap="var(--size-24)">
				<LayoutFlex padding="0" gap="var(--size-20)" align-items="center">
					<Text font="--heading-h3" color="--text-dark-primary" text-align="center">
						Your plan will end after {{ pluralize(count, 'more payment') }}
					</Text>

					<BreakdownCard :rows="rows">
						Your {{ money(q.monthlyPrice) }}/month rate is based on a {{ q.termMonths }}-month commitment. Since you’re
						ending your plan early, {{ recalculated }} will be recalculated{{ recalculatedAt }}. Your remaining
						{{ money(plan?.amount) }} will be split into {{ pluralize(count, 'scheduled payment') }}.
					</BreakdownCard>

					<ConsequenceList :items="consequences" />
				</LayoutFlex>

				<Button full-width :disabled="!plan || busy" :loading="busy" @ds:click="onConfirm">
					<Text font="--text-16-18-semi">Cancel after {{ pluralize(count, 'payment') }}</Text>
				</Button>
			</LayoutFlex>
		</template>
	</LayoutScreen>
	<!-- The cancel call failed (500 / no connection): nothing was cancelled. "Try again" re-runs it here. -->
	<Modal :visible="cancelFailed" fullscreen animation="none">
		<CancelErrorScreen :step="step.title" @retry="retryConfirm" />
	</Modal>
</template>

<script setup lang="ts">
// /cancel/commitment/end-after — Figma `kova cancel flow6` 6590:15500, now 7415:18411: split the exit fee over the
// remaining payments of the current supply (`installments_fee` → `cancellation_type: supply_end`).
// The schedule is snapshotted into the outcome before the call: the option is gone from the
// subscription once the cancel lands.
//
// A 409 is "cancellation at supply end is not offered" — and our own earlier request (a double
// submit, a retry after a timeout) answers the same. So: re-read; `cancellation_type: supply_end`
// means it went through → the receipt; anything else → back to the options as they are now.
//
// Analytics (AL-15098): CANCEL_AFTER_SCHEDULED_PAYMENTS_SUMMARY with `data.commitment` (`payments_left`);
// the cancel is a `supply_end` SubscriptionStateChanged — the fee in instalments, the plan ends with the supply.
//
// TODO(DEV-35727): "Your plan ends on {date}" is an open copy question (the backend ends access at
// `access_until`, after the final payment's period).
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { Button, LayoutFlex, LayoutScreen, Modal, Text } from '@simple-life-app/growth-ui-kova'
import CancelErrorScreen from '@/components/cancel/CancelErrorScreen.vue'
import CancelHeader from '@/components/CancelHeader.vue'
import BreakdownCard, { type BreakdownRow } from '@/components/cancel/BreakdownCard.vue'
import ConsequenceList from '@/components/cancel/ConsequenceList.vue'
import { CancellationNotOfferedError } from '@/api/medBilling'
import { COMMITMENT_ROUTES, feeBreakdown } from '@/cancelFlow/commitment'
import { formatDate, formatMoney, joinDates, pluralize } from '@/cancelFlow/format'
import { useCancelFlowStore } from '@/store/cancelFlow'
import { useSubscriptionStore } from '@/store/subscription'
import { useSurveyStore } from '@/store/survey'
import { STEP_TITLE, commitmentStepData, stateChangeOnCancel, useCancelStepLogging } from '@/analytics'

const router = useRouter()
const flow = useCancelFlowStore()
const subs = useSubscriptionStore()
const survey = useSurveyStore()

const q = computed(() => flow.quotes)
const plan = computed(() => q.value.supplyEnd)
const installments = computed(() => plan.value?.installments ?? [])
const count = computed(() => installments.value.length)
const busy = ref(false)
const cancelFailed = ref(false)

function money(amount: number | null | undefined) {
	return formatMoney(amount, q.value.currency)
}

const breakdown = computed(() => feeBreakdown(plan.value, q.value.monthlyPrice))

const step = useCancelStepLogging(STEP_TITLE.CANCEL_AFTER_SCHEDULED_PAYMENTS_SUMMARY, {
	getCommitment: () =>
		commitmentStepData(subs.subscription, flow.kind, plan.value, {
			fee: q.value.endToday?.amount ?? 0,
			paymentsLeft: count.value,
		}),
})

// "the 3 months of medication you’ve received … at $249/month" when the backend priced it so.
const recalculated = computed(() =>
	breakdown.value
		? `the ${pluralize(breakdown.value.shippedMonths, 'month')} of medication you’ve received`
		: 'the medication you’ve received',
)
const recalculatedAt = computed(() => (breakdown.value ? ` at ${money(breakdown.value.rate)}/month` : ''))

const rows = computed<BreakdownRow[]>(() => {
	const b = breakdown.value
	const total: BreakdownRow = {
		label: `Total remaining (split into ${pluralize(count.value, 'payment')})`,
		value: money(plan.value?.amount),
		emphasis: true,
	}
	if (!b) return [total]
	return [
		{ label: `${pluralize(b.unpaidMonths, 'month')} unpaid × ${money(b.rate)}`, value: money(b.unpaidMonths * b.rate) },
		{ label: `${pluralize(b.paidMonths, 'month')} recalculation`, value: money(b.recalculation) },
		total,
	]
})

const consequences = computed(() => {
	const list = installments.value
	if (!list.length) return []
	// The plan ends with its last payment (as the receipt says); care stays until the supply runs out.
	return [
		`Your plan ends on ${formatDate(list[list.length - 1].date)}, with no further medication shipments`,
		`Your remaining payments of ${money(list[0].amount)} are scheduled for ${joinDates(
			list.map((i) => i.date),
			'medium',
		)}`,
		'If you restart later, you’ll need a new intake and clinician approval before anything ships',
		`Your provider and the KOVA app stay available until ${formatDate(plan.value?.access_until, 'short')}`,
	]
})

async function onConfirm() {
	const p = plan.value
	if (!p || busy.value) return
	busy.value = true
	step.record(['cancel_after_scheduled_payments'])
	const changed = stateChangeOnCancel('supply_end')
	const outcome = {
		kind: 'ending_after_payments' as const,
		installments: p.installments ?? [],
		currency: q.value.currency,
		endsOn: p.access_until ?? null,
	}
	try {
		await subs.cancel({ cancellationType: 'supply_end' })
	} catch (err) {
		busy.value = false
		if (!(err instanceof CancellationNotOfferedError)) {
			cancelFailed.value = true
			return
		}
		await subs.refreshSubscription()
		if (subs.subscription?.cancellation_type !== 'supply_end') {
			router.replace(COMMITMENT_ROUTES.options)
			return
		}
	}
	busy.value = false
	changed()
	flow.setOutcome(outcome)
	survey.reset()
	router.push('/cancel/done')
}

function retryConfirm() {
	cancelFailed.value = false
	onConfirm()
}

// The quote can vanish between screens (a payment ran, the card was removed): nothing to confirm.
if (!plan.value) router.replace(COMMITMENT_ROUTES.options)
</script>
